// src/stores/auth.js
import { defineStore } from 'pinia';
import Cookies from 'js-cookie';
import axios from 'axios';
import { setGAUserId, trackGAEvent } from '@/services/analyticsService';
import { useStepperStore } from './stepper';
import { useLCAStore } from './lca';
import { useChatbotStore } from './chatbot';

export const useAuthStore = defineStore('auth', {
  // State properties
  state: () => ({
    isLoggedIn: !!Cookies.get('access_token'),
    token: Cookies.get('access_token') || null,
    gaUserId: localStorage.getItem('ga_user_id') || null, // ga_user_id aus localStorage laden
  }),
  
  // Actions to modify the state
  actions: {
    async login(email, password) {
      try {
        // Reset other stores before login to ensure no old data remains
        const stepperStore = useStepperStore();
        const lcaStore = useLCAStore();
        const chatbotStore = useChatbotStore();
        stepperStore.resetStore();
        lcaStore.resetStore();
        chatbotStore.resetStore();
        console.log('All stores reset before new login');
        
        const response = await axios.post('/login', {
          email,
          password,
        });

        const token = response.data.access_token;
        const gaUserIdFromResponse = response.data.ga_user_id;
        
        // Save the token in cookies
        Cookies.set('access_token', token, { expires: 10/24 }); // 10 hours matching backend config
        
        // Update the login status
        this.isLoggedIn = true;
        this.token = token;
        
        // Google Analytics User-ID setzen
        if (gaUserIdFromResponse) {
          this.gaUserId = gaUserIdFromResponse;
          localStorage.setItem('ga_user_id', gaUserIdFromResponse);
          setGAUserId(gaUserIdFromResponse);
          
          // Optional: Login-Event tracken
          trackGAEvent('user_login', {
            method: 'credentials'
          });
        } else {
          this.clearGAUserIdLocallyAndInGA();
        }
        
        return true;
      } catch (error) {
        console.error('Login error:', error);
        this.clearGAUserIdLocallyAndInGA();
        throw error;
      }
    },
    
    logout() {
      // Remove the token from cookies
      Cookies.remove('access_token');
      
      // Google Analytics User-ID entfernen
      this.clearGAUserIdLocallyAndInGA();
      
      // Optional: Logout-Event tracken
      trackGAEvent('user_logout');
      
      // Update the login status
      this.isLoggedIn = false;
      this.token = null;
      
      // Reset all other stores to clear user data
      const stepperStore = useStepperStore();
      const lcaStore = useLCAStore();
      const chatbotStore = useChatbotStore();
      stepperStore.resetStore();
      lcaStore.resetStore();
      chatbotStore.resetStore();
      console.log('All stores have been reset after logout');
    },
    
    // Hilfsmethode zum lokalen Löschen und Entfernen der User-ID in GA
    clearGAUserIdLocallyAndInGA() {
      localStorage.removeItem('ga_user_id');
      this.gaUserId = null;
      setGAUserId(null);
    },
    
    // Initialisiert die Google Analytics User-ID beim Laden der App
    initGAUserId() {
      if (this.isLoggedIn && this.gaUserId) {
        setGAUserId(this.gaUserId);
        console.log('Google Analytics User-ID wiederhergestellt:', this.gaUserId);
      } else if (this.isLoggedIn && !this.gaUserId) {
        console.warn("Eingeloggt, aber keine Google Analytics User-ID im lokalen Speicher gefunden.");
        setGAUserId(null);
      } else {
        setGAUserId(null);
      }
    },
    
    // Legacy method to maintain compatibility with existing code
    loginWithToken(token, gaUserId) {
      // Reset other stores before login to ensure no old data remains
      const stepperStore = useStepperStore();
      const lcaStore = useLCAStore();
      const chatbotStore = useChatbotStore();
      stepperStore.resetStore();
      lcaStore.resetStore();
      chatbotStore.resetStore();
      console.log('All stores reset before token login');
      
      // Save the token in cookies
      Cookies.set('access_token', token, { expires: 10/24 });
      
      // Update the login status
      this.isLoggedIn = true;
      this.token = token;
      
      // Google Analytics User-ID setzen, falls übergeben
      if (gaUserId) {
        this.gaUserId = gaUserId;
        localStorage.setItem('ga_user_id', gaUserId);
        setGAUserId(gaUserId);
      }
    },

    async register(registrationData) {
      try {
        const response = await axios.post('/register', registrationData);
        
        // Track registration event
        trackGAEvent('user_registration', {
          account_type: registrationData.accountType
        });
        
        return response.data;
      } catch (error) {
        console.error('Registration error:', error);
        throw error;
      }
    },

    async requestPasswordReset(email) {
      try {
        await axios.post('/reset-password', { email });
        return true;
      } catch (error) {
        console.error('Password reset request error:', error);
        throw error;
      }
    },

    async deleteAccount() {
      try {
        // Delete account via API
        await axios.delete('/user/account');
        
        // Track deletion event before clearing user data
        trackGAEvent('account_deleted');
        
        // Clear all user data (logout will also reset other stores)
        this.logout();
        
        // Redirect to home page
        window.location.href = '/';
        
        return true;
      } catch (error) {
        console.error('Account deletion error:', error);
        throw error;
      }
    },

    // Check if a route requires authentication
    checkRouteRequiresAuth(route) {
      return route.meta && route.meta.requiresAuth;
    }
  },
});